/**
 * `expenses` endpoint wrappers.
 *
 * Field shapes are derived from `serializeExpense`
 * (refactor/backend/src/modules/expenses/serializers.ts) — see
 * src/types/expense.ts for the full derivation notes. Route paths and
 * ordering are verified against refactor/backend/src/modules/expenses/routes.ts.
 *
 * All writes require a manager role (LANDLORD/AGENT/SUPERAGENT); reads are
 * scoped to whatever the caller's role sees.
 */
import { apiClient } from '../client';
import { EXPENSES, EXPENSES_SUMMARY } from './paths';
import type { Paginated } from '../types';
import type {
  Expense,
  ExpenseCreateRequest,
  ExpensePatchRequest,
  ExpenseSummaryRow,
} from '../../types/expense';

/** `GET expenses/` — paginated, scoped to the caller; `property`/`category` are server-side filters. */
export async function listExpenses(params?: {
  page?: number | undefined;
  property?: number | undefined;
  category?: string | undefined;
}): Promise<Paginated<Expense>> {
  const response = await apiClient.get<Paginated<Expense>>(EXPENSES, { params });
  return response.data;
}

/** `GET expenses/{id}/`. */
export async function getExpense(id: number): Promise<Expense> {
  const response = await apiClient.get<Expense>(`${EXPENSES}${id}/`);
  return response.data;
}

/** `POST expenses/` — the target property must be owned by or assigned to the caller (or caller is SUPERAGENT). */
export async function createExpense(payload: ExpenseCreateRequest): Promise<Expense> {
  const response = await apiClient.post<Expense>(EXPENSES, payload);
  return response.data;
}

/** `PATCH expenses/{id}/` — partial update. */
export async function updateExpense(id: number, payload: ExpensePatchRequest): Promise<Expense> {
  const response = await apiClient.patch<Expense>(`${EXPENSES}${id}/`, payload);
  return response.data;
}

/** `DELETE expenses/{id}/`. */
export async function deleteExpense(id: number): Promise<void> {
  await apiClient.delete(`${EXPENSES}${id}/`);
}

/**
 * `GET expenses/summary/` — a BARE ARRAY of `{category, total}`, grouped by
 * category, NOT the `Paginated<T>` envelope. `total` is a raw JSON number,
 * unlike the quantised decimal STRINGS the rest of this module returns.
 * The same `property`/`category` filters as `listExpenses` apply here too.
 */
export async function getExpenseSummary(params?: {
  property?: number | undefined;
  category?: string | undefined;
}): Promise<ExpenseSummaryRow[]> {
  const response = await apiClient.get<ExpenseSummaryRow[]>(EXPENSES_SUMMARY, { params });
  return response.data;
}
